import { createRouter, createWebHistory } from "vue-router";
import { useWindowSize } from "@vueuse/core";

import AuthRoutes from "@/modules/Authorization/routes";
import ProfileRoutes from "@/modules/Profile/routes";
import PublicProfileRoutes from "@/modules/PublicProfile/routes";
import { useAuthStore } from "@/modules/Authorization/store";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    ...AuthRoutes,
    ...ProfileRoutes,
    ...PublicProfileRoutes,
    {
      path: "/:pathMatch(.*)*",
      name: "404",
      meta: {
        layout: "error",
        public: true,
      },
      component: () => import("@/layout/LError.vue"),
    },
  ],
});

const { width } = useWindowSize();

/**
 * Everything needs a token except routes marked `public`.
 *
 * This used to key off `meta.auth`, which read as "requires auth" but was set on the sign-in and
 * policy pages — and also on every profile page, so the check never fired and the whole profile was
 * reachable with no token at all. `public` says what it means and defaults to closed.
 */
router.beforeEach(async (to) => {
  const authStore = useAuthStore();
  authStore.initTokens();

  const signedIn = !!authStore.tokens?.access;

  if (to.path === "/") {
    return signedIn
      ? { name: width.value < 768 ? "Profile" : "ProfileNotifications" }
      : { name: "Login" };
  }

  if (to.meta?.public) {
    // A signed-in user landing on sign-in goes straight to their profile.
    if (to.name === "Login" && signedIn) {
      return { name: width.value < 768 ? "Profile" : "ProfileNotifications" };
    }
    return true;
  }

  if (!signedIn) {
    return { name: "Login" };
  }

  return true;
});

declare module "vue-router" {
  interface RouteMeta {
    /** Reachable without a token: sign-in, password reset, policy pages, error pages. */
    public?: boolean;
    layout?: string;
    permission?: {
      name: unknown;
      actions: unknown[];
    };
  }
}

export default router;
